import { isKegSku } from 'util/pricing-fields';

/**
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
export function normalizeMailchimpPrice(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (s === '' || s === '-') return null;
  return s;
}

/**
 * Foodstuffs SAP article on a SKU (trimmed; empty / "-" → null).
 * @param {object} sku
 * @returns {string|null}
 */
export function getFoodstuffsNi(sku) {
  if (!sku) return null;
  return normalizeMailchimpPrice(sku.foodstuffs_article_ni ?? sku.foodstuffsArticleNi);
}

/**
 * @param {object} sku
 * @returns {string|null}
 */
export function getFoodstuffsSi(sku) {
  if (!sku) return null;
  return normalizeMailchimpPrice(sku.foodstuffs_article_si ?? sku.foodstuffsArticleSi);
}

export const FOODSTUFFS_PRICING_TYPES = ['foodstuffsNi', 'foodstuffsSi', 'foodstuffsBoth'];

/**
 * @param {string|undefined} pricingType
 * @returns {boolean}
 */
export function isFoodstuffsPricingType(pricingType) {
  return FOODSTUFFS_PRICING_TYPES.includes(pricingType);
}

/**
 * Trade price on a product_sku row for Mailchimp pricing type.
 * Keg off-prem uses on-prem price (same rule as priceFieldGreyOut).
 * Foodstuffs tiers return SAP article string(s); for `foodstuffsBoth` use
 * {@link getFoodstuffsNi} / {@link getFoodstuffsSi} in the snippet.
 *
 * @param {object} sku — API row (snake_case or camelCase)
 * @param {string} pricingType — onPremise | offPremise | … | foodstuffsNi | foodstuffsSi | foodstuffsBoth
 * @returns {string|null}
 */
export function tradePriceForSku(sku, pricingType) {
  if (!sku) return null;
  const g = (a, b) => normalizeMailchimpPrice(sku[a] ?? sku[b]);

  switch (pricingType) {
    case 'onPremise':
      return g('price_on', 'priceOn');
    case 'offPremise':
      if (isKegSku(sku)) {
        return g('price_on', 'priceOn');
      }
      return g('price_off', 'priceOff');
    case 'progressive':
      return g('price_progressive', 'priceProgressive');
    case 'vicSa':
      return g('price_vic_sa', 'priceVicSa');
    case 'qldWa':
      return g('price_qld_wa', 'priceQldWa');
    case 'australia':
      return g('price_au', 'priceAu');
    case 'foodstuffsNi':
      return getFoodstuffsNi(sku);
    case 'foodstuffsSi':
      return getFoodstuffsSi(sku);
    case 'foodstuffsBoth':
      return null;
    default:
      return null;
  }
}

/**
 * Whether the SKU has a value for the selected tier (price or SAP).
 * @param {object} sku
 * @param {string} pricingType
 * @returns {boolean}
 */
export function skuHasTierValue(sku, pricingType) {
  if (!sku) return false;
  if (pricingType === 'foodstuffsNi') return getFoodstuffsNi(sku) != null;
  if (pricingType === 'foodstuffsSi') return getFoodstuffsSi(sku) != null;
  if (pricingType === 'foodstuffsBoth') {
    return getFoodstuffsNi(sku) != null || getFoodstuffsSi(sku) != null;
  }
  return normalizeMailchimpPrice(tradePriceForSku(sku, pricingType)) != null;
}

/**
 * Default SKU keys to pre-select: non-legacy, not invalid_combination.
 *
 * @param {object} product — product with optional `skus` array
 * @returns {number[]}
 */
export function defaultMailchimpSkuKeys(product) {
  const skus = product?.skus;
  if (!Array.isArray(skus)) return [];
  return skus
    .filter((s) => !s.is_legacy)
    .filter((s) => s.validity_status !== 'invalid_combination')
    .map((s) => s.key)
    .filter((k) => k != null);
}
